import React, { useEffect, useRef, useState } from "react";
import PageHeader from "@/components/PageHeader";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Upload, Save, Trash2, MessageSquare, Palette, Building2, Image as ImageIcon, Ticket, Type, Plus, GripVertical, X as XIcon, Monitor, ExternalLink, AlertTriangle, ShieldAlert, Loader2, Download, Archive, UploadCloud, History } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { useBranding } from "@/hooks/useBranding";
import { useAuth } from "@/context/AuthContext";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

function ColorField({ label, value, onChange, testid }) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <div className="flex items-center gap-2">
        <input
          type="color"
          value={value || "#1E3A8A"}
          onChange={(e) => onChange(e.target.value)}
          className="h-11 w-14 rounded-lg border border-slate-200 cursor-pointer bg-white"
          data-testid={`${testid}-picker`}
        />
        <Input
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          className="font-mono flex-1"
          data-testid={testid}
        />
      </div>
    </div>
  );
}

export default function Parametres() {
  const { refresh: refreshBranding } = useBranding();
  const { user } = useAuth();
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/settings");
      setSettings(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  useEffect(() => { load(); }, []);

  const patch = async (payload) => {
    setSaving(true);
    try {
      const { data } = await api.patch("/settings", payload);
      setSettings(data);
      refreshBranding();
      toast.success("Paramètres enregistrés");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  const uploadLogo = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/settings/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setSettings((s) => ({ ...s, logo_data_url: data.logo_data_url }));
      refreshBranding();
      toast.success("Logo mis à jour");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur upload");
    } finally {
      setUploading(false);
    }
  };

  const removeLogo = async () => {
    if (!window.confirm("Supprimer le logo actuel ?")) return;
    try {
      await api.delete("/settings/logo");
      setSettings((s) => ({ ...s, logo_data_url: null }));
      refreshBranding();
      toast.success("Logo supprimé");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  if (!settings) {
    return <div className="text-slate-500">Chargement...</div>;
  }

  const cw = settings.chat_widget || {};

  return (
    <div>
      <PageHeader
        eyebrow="Administration"
        title="Paramètres de l'entreprise"
        description="Personnalisez la marque, les couleurs et le widget de chat de support."
        testid="parametres-header"
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Identité entreprise */}
        <Card className="lg:col-span-2 border border-slate-200 bg-white">
          <CardHeader>
            <div className="text-[10px] uppercase tracking-[0.28em] text-slate-400">Identité</div>
            <CardTitle className="font-display text-xl flex items-center gap-2">
              <Building2 className="w-5 h-5 text-blue-800" /> Marque &amp; support
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Nom de l&apos;entreprise</Label>
                <Input
                  value={settings.company_name || ""}
                  onChange={(e) => setSettings({ ...settings, company_name: e.target.value })}
                  data-testid="param-company-name"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Nom du support (affiché aux clients)</Label>
                <Input
                  value={settings.support_name || ""}
                  onChange={(e) => setSettings({ ...settings, support_name: e.target.value })}
                  data-testid="param-support-name"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-1.5">
                <Label>Email de contact</Label>
                <Input
                  type="email"
                  value={settings.contact_email || ""}
                  onChange={(e) => setSettings({ ...settings, contact_email: e.target.value })}
                  data-testid="param-contact-email"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Téléphone de contact</Label>
                <Input
                  value={settings.contact_phone || ""}
                  onChange={(e) => setSettings({ ...settings, contact_phone: e.target.value })}
                  data-testid="param-contact-phone"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Adresse</Label>
                <Input
                  value={settings.address || ""}
                  onChange={(e) => setSettings({ ...settings, address: e.target.value })}
                  data-testid="param-address"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <ColorField
                label="Couleur principale"
                value={settings.primary_color}
                onChange={(v) => setSettings({ ...settings, primary_color: v })}
                testid="param-primary-color"
              />
              <ColorField
                label="Couleur d&apos;accent"
                value={settings.accent_color}
                onChange={(v) => setSettings({ ...settings, accent_color: v })}
                testid="param-accent-color"
              />
              <ColorField
                label="Couleur secondaire"
                value={settings.secondary_color}
                onChange={(v) => setSettings({ ...settings, secondary_color: v })}
                testid="param-secondary-color"
              />
              <ColorField
                label="Couleur file d&apos;attente (borne / écran)"
                value={settings.queue_color}
                onChange={(v) => setSettings({ ...settings, queue_color: v })}
                testid="param-queue-color"
              />
              <ColorField
                label="Couleur d&apos;arrière-plan"
                value={settings.background_color}
                onChange={(v) => setSettings({ ...settings, background_color: v })}
                testid="param-background-color"
              />
            </div>

            <div className="pt-2">
              <Button
                onClick={() => patch({
                  company_name: settings.company_name,
                  support_name: settings.support_name,
                  contact_email: settings.contact_email,
                  contact_phone: settings.contact_phone,
                  address: settings.address,
                  primary_color: settings.primary_color,
                  accent_color: settings.accent_color,
                  secondary_color: settings.secondary_color,
                  queue_color: settings.queue_color,
                  background_color: settings.background_color,
                })}
                disabled={saving}
                className="bg-blue-900 hover:bg-blue-800 rounded-full"
                data-testid="param-save-identity"
              >
                <Save className="w-4 h-4 mr-2" /> {saving ? "Enregistrement..." : "Enregistrer"}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Logo */}
        <Card className="border border-slate-200 bg-white">
          <CardHeader>
            <div className="text-[10px] uppercase tracking-[0.28em] text-slate-400">Visuel</div>
            <CardTitle className="font-display text-xl flex items-center gap-2">
              <ImageIcon className="w-5 h-5 text-blue-800" /> Logo
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="w-full aspect-video border-2 border-dashed border-slate-200 rounded-xl grid place-items-center bg-slate-50/60 overflow-hidden">
              {settings.logo_data_url ? (
                <img src={settings.logo_data_url} alt="Logo" className="max-h-full max-w-full object-contain" data-testid="param-logo-preview" />
              ) : (
                <div className="text-center text-slate-400 text-sm">
                  <ImageIcon className="w-6 h-6 mx-auto mb-1" />
                  Aucun logo
                </div>
              )}
            </div>
            <input
              ref={inputRef}
              type="file"
              accept="image/*"
              hidden
              onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadLogo(f); e.target.value = ""; }}
              data-testid="param-logo-file"
            />
            <div className="mt-4 flex flex-col gap-2">
              <Button onClick={() => inputRef.current?.click()} disabled={uploading} className="w-full bg-blue-900 hover:bg-blue-800 rounded-full" data-testid="param-logo-upload">
                <Upload className="w-4 h-4 mr-2" /> {uploading ? "Envoi..." : "Uploader un logo"}
              </Button>
              {settings.logo_data_url && (
                <Button variant="outline" onClick={removeLogo} className="w-full" data-testid="param-logo-delete">
                  <Trash2 className="w-4 h-4 mr-2 text-red-500" /> Supprimer
                </Button>
              )}
            </div>
            <p className="text-xs text-slate-500 mt-2">PNG / JPG / SVG · max 800 Ko</p>
          </CardContent>
        </Card>

        {/* Services de la borne */}
        <Card className="lg:col-span-3 border border-slate-200 bg-white">
          <CardHeader>
            <div className="text-[10px] uppercase tracking-[0.28em] text-slate-400">Borne &amp; file d&apos;attente</div>
            <CardTitle className="font-display text-xl flex items-center gap-2">
              <Ticket className="w-5 h-5 text-blue-800" /> Services proposés sur la borne
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ServicesEditor
              services={settings.services || []}
              onChange={(services) => setSettings({ ...settings, services })}
              onSave={(services) => patch({ services })}
              saving={saving}
            />
          </CardContent>
        </Card>

        {/* Messages */}
        <Card className="lg:col-span-3 border border-slate-200 bg-white">
          <CardHeader>
            <div className="text-[10px] uppercase tracking-[0.28em] text-slate-400">Contenu</div>
            <CardTitle className="font-display text-xl flex items-center gap-2">
              <Type className="w-5 h-5 text-blue-800" /> Messages d&apos;accueil (borne &amp; écran)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <MessagesEditor
              messages={settings.borne_messages || {}}
              companyName={settings.company_name || "dkconnect"}
              logoDataUrl={settings.logo_data_url}
              secondaryColor={settings.secondary_color || "#0B1B4B"}
              queueColor={settings.queue_color || "#1E3A8A"}
              onChange={(borne_messages) => setSettings({ ...settings, borne_messages })}
              onSave={(borne_messages) => patch({ borne_messages })}
              saving={saving}
            />
          </CardContent>
        </Card>

        {/* Chat widget */}
        <Card className="lg:col-span-3 border border-slate-200 bg-white">
          <CardHeader>
            <div className="text-[10px] uppercase tracking-[0.28em] text-slate-400">Widget de chat</div>
            <CardTitle className="font-display text-xl flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-blue-800" /> Support client sur votre site
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="space-y-4 lg:col-span-2">
                <div className="flex items-center justify-between p-3 border border-slate-200 rounded-xl bg-slate-50/50">
                  <div>
                    <div className="font-medium text-slate-900">Widget activé</div>
                    <div className="text-xs text-slate-500">Affiche le bouton de chat sur votre site.</div>
                  </div>
                  <Switch
                    checked={!!cw.enabled}
                    onCheckedChange={(v) => setSettings({ ...settings, chat_widget: { ...cw, enabled: v } })}
                    data-testid="param-chat-enabled"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label>Titre du widget</Label>
                    <Input
                      value={cw.title || ""}
                      onChange={(e) => setSettings({ ...settings, chat_widget: { ...cw, title: e.target.value } })}
                      data-testid="param-chat-title"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Position</Label>
                    <Select value={cw.position || "bottom-right"} onValueChange={(v) => setSettings({ ...settings, chat_widget: { ...cw, position: v } })}>
                      <SelectTrigger data-testid="param-chat-position"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="bottom-right">Bas droit</SelectItem>
                        <SelectItem value="bottom-left">Bas gauche</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label>Message de bienvenue</Label>
                  <Textarea
                    rows={3}
                    value={cw.welcome_message || ""}
                    onChange={(e) => setSettings({ ...settings, chat_widget: { ...cw, welcome_message: e.target.value } })}
                    data-testid="param-chat-welcome"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <ColorField
                    label="Couleur principale du widget"
                    value={cw.primary_color}
                    onChange={(v) => setSettings({ ...settings, chat_widget: { ...cw, primary_color: v } })}
                    testid="param-chat-primary"
                  />
                  <ColorField
                    label="Couleur d&apos;accent"
                    value={cw.accent_color}
                    onChange={(v) => setSettings({ ...settings, chat_widget: { ...cw, accent_color: v } })}
                    testid="param-chat-accent"
                  />
                </div>

                <Button
                  onClick={() => patch({ chat_widget: cw })}
                  disabled={saving}
                  className="bg-blue-900 hover:bg-blue-800 rounded-full"
                  data-testid="param-chat-save"
                >
                  <Save className="w-4 h-4 mr-2" /> Enregistrer le widget
                </Button>
              </div>

              {/* Preview */}
              <div>
                <Label className="text-[10px] uppercase tracking-[0.28em] text-slate-400">Aperçu</Label>
                <div className="mt-2 relative w-full h-96 rounded-2xl border border-slate-200 bg-gradient-to-br from-slate-100 to-slate-200 overflow-hidden">
                  <div className="absolute inset-0 grid place-items-center text-slate-400 text-xs">
                    <Palette className="w-5 h-5" /> Site vitrine
                  </div>
                  <div className={`absolute ${cw.position === "bottom-left" ? "bottom-4 left-4" : "bottom-4 right-4"} space-y-2 max-w-[240px]`}>
                    <div
                      className="rounded-2xl p-3 shadow-xl text-white text-sm"
                      style={{ background: cw.primary_color || "#1E3A8A" }}
                      data-testid="param-chat-preview-bubble"
                    >
                      <div className="text-[10px] uppercase tracking-widest opacity-80 mb-1">{cw.title || "Support"}</div>
                      <div>{cw.welcome_message || "Bonjour !"}</div>
                    </div>
                    <button
                      className="ml-auto flex w-14 h-14 rounded-full items-center justify-center shadow-2xl text-white"
                      style={{ background: `linear-gradient(135deg, ${cw.primary_color || "#1E3A8A"}, ${cw.accent_color || "#8B5CF6"})` }}
                      data-testid="param-chat-preview-btn"
                    >
                      <MessageSquare className="w-6 h-6" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {user?.role === "admin" && (
          <DangerZone testid="param-danger-zone" />
        )}
      </div>
    </div>
  );
}

// ---------- Sub-editors ----------

const TONE_OPTIONS = [
  "from-blue-500 to-blue-700",
  "from-orange-500 to-red-600",
  "from-emerald-500 to-teal-700",
  "from-violet-500 to-purple-700",
  "from-cyan-500 to-blue-700",
  "from-slate-500 to-slate-700",
  "from-pink-500 to-rose-700",
  "from-amber-500 to-orange-700",
];

function ServicesEditor({ services, onChange, onSave, saving }) {
  const [items, setItems] = useState(services);
  useEffect(() => { setItems(services); }, [services]);

  const update = (idx, patch) => {
    const next = items.map((s, i) => (i === idx ? { ...s, ...patch } : s));
    setItems(next);
    onChange(next);
  };
  const remove = (idx) => {
    const next = items.filter((_, i) => i !== idx);
    setItems(next);
    onChange(next);
  };
  const add = () => {
    const next = [
      ...items,
      { key: `service_${Date.now()}`, label: "Nouveau service", description: "", prefix: "N", tone: TONE_OPTIONS[0], enabled: true },
    ];
    setItems(next);
    onChange(next);
  };

  return (
    <div className="space-y-3">
      <p className="text-sm text-slate-500">
        Ces services apparaissent sur la borne physique. Le préfixe est utilisé pour générer les numéros de ticket (ex : « R-042 » pour Réclamations).
      </p>
      <div className="space-y-3">
        {items.map((s, idx) => (
          <div
            key={s.key || `svc-${idx}`}
            className="grid grid-cols-1 md:grid-cols-12 gap-3 items-center p-3 border border-slate-200 rounded-xl bg-slate-50/40"
            data-testid={`service-row-${idx}`}
          >
            <div className="md:col-span-3">
              <Label className="text-xs text-slate-500">Libellé</Label>
              <Input value={s.label || ""} onChange={(e) => update(idx, { label: e.target.value })} data-testid={`service-label-${idx}`} />
            </div>
            <div className="md:col-span-3">
              <Label className="text-xs text-slate-500">Description</Label>
              <Input value={s.description || ""} onChange={(e) => update(idx, { description: e.target.value })} data-testid={`service-desc-${idx}`} />
            </div>
            <div className="md:col-span-1">
              <Label className="text-xs text-slate-500">Préfixe</Label>
              <Input value={s.prefix || ""} maxLength={2} onChange={(e) => update(idx, { prefix: e.target.value.toUpperCase() })} className="font-mono uppercase" data-testid={`service-prefix-${idx}`} />
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs text-slate-500">Clé interne</Label>
              <Input value={s.key || ""} onChange={(e) => update(idx, { key: e.target.value })} className="font-mono text-xs" data-testid={`service-key-${idx}`} />
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs text-slate-500">Couleur</Label>
              <Select value={s.tone || TONE_OPTIONS[0]} onValueChange={(v) => update(idx, { tone: v })}>
                <SelectTrigger data-testid={`service-tone-${idx}`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TONE_OPTIONS.map((t) => (
                    <SelectItem key={t} value={t}>
                      <div className="flex items-center gap-2">
                        <span className={`inline-block w-6 h-3 rounded bg-gradient-to-r ${t}`} />
                        <span className="text-xs">{t.replace("from-", "").split(" ")[0]}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-1 flex flex-col items-center gap-1">
              <Label className="text-xs text-slate-500">Actif</Label>
              <Switch
                checked={s.enabled !== false}
                onCheckedChange={(v) => update(idx, { enabled: v })}
                data-testid={`service-enabled-${idx}`}
              />
            </div>
            <div className="md:col-span-12 flex justify-end">
              <button
                type="button"
                onClick={() => remove(idx)}
                className="text-red-600 hover:text-red-700 text-xs inline-flex items-center gap-1"
                data-testid={`service-remove-${idx}`}
              >
                <XIcon className="w-3 h-3" /> Retirer
              </button>
            </div>
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between pt-2">
        <Button variant="outline" onClick={add} className="rounded-full" data-testid="service-add-btn">
          <Plus className="w-4 h-4 mr-2" /> Ajouter un service
        </Button>
        <Button
          onClick={() => onSave(items)}
          disabled={saving}
          className="bg-blue-900 hover:bg-blue-800 rounded-full"
          data-testid="service-save-btn"
        >
          <Save className="w-4 h-4 mr-2" /> {saving ? "Enregistrement…" : "Enregistrer les services"}
        </Button>
      </div>
    </div>
  );
}

const MESSAGE_FIELDS = [
  { key: "welcome_title", label: "Titre d'accueil (borne)", hint: "Utilisez {company} pour insérer le nom de l'entreprise.", multiline: false },
  { key: "welcome_subtitle", label: "Sous-titre d'accueil (borne)", multiline: false },
  { key: "borne_header", label: "En-tête borne (petite ligne au-dessus du titre)", multiline: false },
  { key: "display_header", label: "En-tête écran d'affichage", multiline: false },
  { key: "ticket_thanks", label: "Message de confirmation ticket", multiline: false },
  { key: "ticket_hint", label: "Astuce imprimée / affichée sur le ticket", multiline: true },
  { key: "marquee", label: "Bandeau défilant (écran d'affichage)", hint: "Utilisez {company} pour insérer le nom de l'entreprise. Séparez les phrases par ·", multiline: true },
];

function interpolate(text, vars) {
  if (!text) return "";
  return text.replace(/\{(\w+)\}/g, (_, k) => vars[k] || `{${k}}`);
}

function MessagesEditor({ messages, companyName, logoDataUrl, secondaryColor, queueColor, onChange, onSave, saving }) {
  const [items, setItems] = useState(messages);
  useEffect(() => { setItems(messages); }, [messages]);

  const update = (key, val) => {
    const next = { ...items, [key]: val };
    setItems(next);
    onChange(next);
  };

  const previewMarquee = interpolate(
    items.marquee ||
      "Bienvenue chez {company} — merci de patienter dans la salle d'attente · Votre numéro s'affiche ici quand ce sera votre tour ·",
    { company: companyName },
  );
  const previewTitle = interpolate(items.welcome_title || "Bienvenue chez {company}", { company: companyName });
  const previewSubtitle = items.welcome_subtitle || "Sélectionnez votre service pour prendre un ticket";
  const displayHeader = items.display_header || "Écran d'affichage — salle d'attente";

  return (
    <div className="space-y-5">
      <p className="text-sm text-slate-500">
        Tous les textes affichés sur la borne et l&apos;écran d&apos;affichage. Ces messages seront visibles par vos visiteurs.
        Utilisez <code className="px-1.5 py-0.5 bg-slate-100 rounded font-mono text-[11px]">{"{company}"}</code> pour insérer automatiquement le nom de votre entreprise.
      </p>

      {/* Live preview de l'écran d'affichage */}
      <div className="rounded-2xl overflow-hidden border border-slate-200 bg-slate-50" data-testid="messages-preview">
        <div className="px-4 py-2 border-b border-slate-200 flex items-center justify-between bg-white">
          <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 inline-flex items-center gap-1.5">
            <Monitor className="w-3 h-3" /> Aperçu — écran d&apos;affichage
          </div>
          <a
            href="/ecran-affichage"
            target="_blank"
            rel="noreferrer"
            className="text-[11px] text-blue-800 hover:text-blue-900 inline-flex items-center gap-1"
            data-testid="messages-preview-open-display"
          >
            Ouvrir en plein écran <ExternalLink className="w-3 h-3" />
          </a>
        </div>
        <div
          className="relative p-5 min-h-[180px]"
          style={{ background: `linear-gradient(135deg, ${queueColor} 0%, ${secondaryColor} 55%, #2E1065 100%)` }}
        >
          {logoDataUrl && (
            <div className="absolute inset-0 grid place-items-center opacity-[0.08] pointer-events-none">
              <img src={logoDataUrl} alt="" className="w-52 object-contain" />
            </div>
          )}
          <div className="relative z-10 flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-white/10 border border-white/15 grid place-items-center overflow-hidden shrink-0">
              {logoDataUrl ? (
                <img src={logoDataUrl} alt="Logo" className="w-full h-full object-contain p-1" />
              ) : (
                <Building2 className="w-6 h-6 text-white" />
              )}
            </div>
            <div>
              <div className="font-display font-bold text-lg text-white tracking-tight">{companyName}</div>
              <div className="text-[9px] uppercase tracking-[0.28em] text-blue-200">{displayHeader}</div>
            </div>
          </div>
          <div className="relative z-10 mt-8 mb-3">
            <div className="text-blue-200/80 uppercase tracking-widest text-[10px] mb-1">Ticket appelé</div>
            <div className="font-display text-4xl font-bold text-white/40">A-042</div>
          </div>
          <div className="relative z-10 mt-4 rounded-lg bg-white/[0.08] border border-white/10 px-3 py-2 overflow-hidden">
            <div className="text-blue-100/85 text-xs font-mono truncate" data-testid="preview-marquee">
              {previewMarquee}
            </div>
          </div>
        </div>
      </div>

      {/* Preview borne */}
      <div className="rounded-2xl border border-slate-200 p-4 bg-white flex items-center gap-4">
        <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 shrink-0">
          <Ticket className="w-3 h-3 inline mr-1" /> Borne
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[9px] uppercase tracking-widest text-slate-400">{items.borne_header || "Borne — file d'attente"}</div>
          <div className="font-display text-xl font-bold text-slate-900 truncate" data-testid="preview-welcome-title">{previewTitle}</div>
          <div className="text-xs text-slate-500 truncate">{previewSubtitle}</div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {MESSAGE_FIELDS.map((f) => (
          <div key={f.key} className={f.multiline ? "md:col-span-2 space-y-1.5" : "space-y-1.5"}>
            <Label>{f.label}</Label>
            {f.multiline ? (
              <Textarea
                rows={2}
                value={items[f.key] || ""}
                onChange={(e) => update(f.key, e.target.value)}
                data-testid={`msg-${f.key}`}
              />
            ) : (
              <Input
                value={items[f.key] || ""}
                onChange={(e) => update(f.key, e.target.value)}
                data-testid={`msg-${f.key}`}
              />
            )}
            {f.hint && <p className="text-[11px] text-slate-400">{f.hint}</p>}
          </div>
        ))}
      </div>

      <div className="flex justify-end pt-2">
        <Button
          onClick={() => onSave(items)}
          disabled={saving}
          className="bg-blue-900 hover:bg-blue-800 rounded-full"
          data-testid="msg-save-btn"
        >
          <Save className="w-4 h-4 mr-2" /> {saving ? "Enregistrement…" : "Enregistrer les messages"}
        </Button>
      </div>
    </div>
  );
}


// ---------- Danger Zone (admin only) ----------

function DangerZone({ testid }) {
  const [open, setOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  // Backup / restore state
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [importMode, setImportMode] = useState("merge");
  const restoreInputRef = useRef(null);

  // History reset state
  const [historyResetting, setHistoryResetting] = useState(false);
  const [historyResetResult, setHistoryResetResult] = useState(null);

  const CONFIRM_STRING = "RESET DKCONNECT";

  const runHistoryReset = async () => {
    if (!window.confirm("Vider l'historique et le journal d'audit ? Cette action est irréversible mais n'affecte pas vos données métier.")) return;
    setHistoryResetting(true);
    setHistoryResetResult(null);
    try {
      const { data } = await api.post("/setup/reset-history");
      setHistoryResetResult(data);
      toast.success(`Historique & audit vidés — ${data.total} entrée(s) supprimée(s)`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setHistoryResetting(false);
    }
  };

  const downloadBackup = async () => {
    setExporting(true);
    try {
      const res = await api.get("/backup/export", { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const stamp = new Date().toISOString().slice(0, 16).replace(/[-:T]/g, "");
      const link = document.createElement("a");
      link.href = url;
      link.download = `dkconnect_backup_${stamp}.json`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      toast.success("Sauvegarde téléchargée");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Impossible d'exporter la sauvegarde");
    } finally {
      setExporting(false);
    }
  };

  const uploadBackup = async (file) => {
    if (!file) return;
    setImporting(true);
    setImportResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/backup/import?mode=${importMode}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setImportResult(data);
      toast.success(`Restauration terminée — ${data.total_restored} document(s) importé(s)`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Restauration impossible");
    } finally {
      setImporting(false);
    }
  };

  const runReset = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/setup/factory-reset", { confirm: CONFIRM_STRING });
      setResult(data);
      toast.success("Remise à zéro effectuée avec succès");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de la remise à zéro");
    } finally {
      setRunning(false);
    }
  };

  const totalDeleted = result
    ? Object.values(result.deleted || {}).reduce(
        (sum, v) => sum + (typeof v === "number" ? v : 0),
        0,
      )
    : 0;

  return (
    <>
      {/* Sauvegarde / restauration */}
      <Card className="lg:col-span-3 border-blue-200 bg-blue-50/30 dark:bg-blue-950/10 dark:border-blue-900" data-testid="param-backup-zone">
        <CardHeader>
          <div className="text-[10px] uppercase tracking-[0.28em] text-blue-700 dark:text-blue-300">Sauvegarde</div>
          <CardTitle className="font-display text-xl flex items-center gap-2 text-blue-900 dark:text-blue-200">
            <Archive className="w-5 h-5" /> Backup complet & restauration
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-slate-700 dark:text-slate-300 mb-4">
            Téléchargez un fichier JSON contenant l&apos;ensemble de vos données métier. Réutilisez ce fichier pour restaurer votre base à tout moment — indispensable avant toute action de remise à zéro.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="rounded-lg border border-blue-200 dark:border-blue-900 bg-white dark:bg-slate-900 p-4">
              <div className="flex items-center gap-2 mb-2">
                <Download className="w-4 h-4 text-blue-700 dark:text-blue-300" />
                <div className="font-semibold text-slate-900 dark:text-slate-100 text-sm">Télécharger une sauvegarde</div>
              </div>
              <p className="text-xs text-slate-500 dark:text-slate-400 mb-3">
                Export complet : dossiers, réclamations, appels, clients, tickets, documents, notifications, historique, prospects…
              </p>
              <Button
                onClick={downloadBackup}
                disabled={exporting}
                className="w-full bg-blue-900 hover:bg-blue-800 rounded-full"
                data-testid="backup-export-btn"
              >
                {exporting ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Export en cours…</>
                ) : (
                  <><Download className="w-4 h-4 mr-2" /> Télécharger le backup JSON</>
                )}
              </Button>
            </div>

            <div className="rounded-lg border border-blue-200 dark:border-blue-900 bg-white dark:bg-slate-900 p-4">
              <div className="flex items-center gap-2 mb-2">
                <UploadCloud className="w-4 h-4 text-blue-700 dark:text-blue-300" />
                <div className="font-semibold text-slate-900 dark:text-slate-100 text-sm">Restaurer depuis un fichier</div>
              </div>
              <div className="space-y-2 mb-3">
                <Label className="text-xs text-slate-500 dark:text-slate-400">Mode</Label>
                <Select value={importMode} onValueChange={setImportMode}>
                  <SelectTrigger className="h-9" data-testid="backup-import-mode">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="merge">Fusion — n&apos;ajoute que les données absentes</SelectItem>
                    <SelectItem value="replace">Remplacer — supprime tout puis restaure</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <input
                ref={restoreInputRef}
                type="file"
                accept="application/json,.json"
                hidden
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) uploadBackup(f);
                  e.target.value = "";
                }}
                data-testid="backup-import-file"
              />
              <Button
                onClick={() => restoreInputRef.current?.click()}
                disabled={importing}
                variant="outline"
                className="w-full border-blue-300 text-blue-900 hover:bg-blue-50 rounded-full"
                data-testid="backup-import-btn"
              >
                {importing ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Restauration…</>
                ) : (
                  <><UploadCloud className="w-4 h-4 mr-2" /> Choisir un fichier de sauvegarde</>
                )}
              </Button>
              {importResult && (
                <div className="mt-3 text-xs bg-emerald-50 dark:bg-emerald-950/20 border border-emerald-200 dark:border-emerald-900 rounded p-2 text-emerald-800 dark:text-emerald-300" data-testid="backup-import-result">
                  <b>{importResult.total_restored}</b> document(s) restauré(s) — mode <b>{importResult.mode}</b>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Réinitialisation ciblée — historique / audit */}
      <Card className="lg:col-span-3 border-amber-200 bg-amber-50/30 dark:bg-amber-950/10 dark:border-amber-900" data-testid="param-history-reset">
        <CardHeader>
          <div className="text-[10px] uppercase tracking-[0.28em] text-amber-700 dark:text-amber-400">Maintenance</div>
          <CardTitle className="font-display text-xl flex items-center gap-2 text-amber-900 dark:text-amber-200">
            <History className="w-5 h-5" /> Vider l&apos;historique &amp; le journal d&apos;audit
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-slate-700 dark:text-slate-300 mb-4">
            Supprime uniquement les entrées d&apos;historique et le journal d&apos;audit (traces d&apos;actions passées).
            Vos dossiers, réclamations, appels, tickets, clients et utilisateurs ne sont <b>pas affectés</b>.
            Utile pour désencombrer la base après une longue période d&apos;activité.
          </p>
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
            <Button
              onClick={runHistoryReset}
              disabled={historyResetting}
              className="bg-amber-600 hover:bg-amber-700 text-white rounded-full"
              data-testid="history-reset-btn"
            >
              {historyResetting ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Nettoyage…</>
              ) : (
                <><Trash2 className="w-4 h-4 mr-2" /> Vider historique &amp; audit</>
              )}
            </Button>
            {historyResetResult && (
              <div className="text-xs bg-emerald-50 dark:bg-emerald-950/20 border border-emerald-200 dark:border-emerald-900 rounded px-3 py-2 text-emerald-800 dark:text-emerald-300" data-testid="history-reset-result">
                <b>{historyResetResult.total}</b> entrée(s) supprimée(s) —
                <span className="text-slate-500 ml-1">historique : {historyResetResult.deleted.history}, audit : {historyResetResult.deleted.audit_log}</span>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Zone danger — reset */}
      <Card className="lg:col-span-3 border-red-200 bg-red-50/40 dark:bg-red-950/10 dark:border-red-900" data-testid={testid}>
        <CardHeader>
          <div className="text-[10px] uppercase tracking-[0.28em] text-red-600 dark:text-red-400">Zone danger</div>
          <CardTitle className="font-display text-xl flex items-center gap-2 text-red-800 dark:text-red-300">
            <ShieldAlert className="w-5 h-5" /> Remise à zéro des données
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="mb-4 p-3 rounded-lg bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-900 text-xs text-amber-900 dark:text-amber-200 flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
            <div>
              <b>Astuce :</b> téléchargez d&apos;abord une sauvegarde ci-dessus ↑ avant de lancer la remise à zéro. Vous pourrez restaurer vos données en cas de suppression accidentelle.
            </div>
          </div>
          <p className="text-sm text-slate-700 dark:text-slate-300 mb-4">
            Supprime définitivement toutes les données métier (dossiers, réclamations, appels, tickets, clients, discussions, documents, notifications, historique, prospects, base de connaissances).
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
            <div className="rounded-lg border border-red-200 dark:border-red-900 bg-white dark:bg-slate-900 p-3">
              <div className="text-[10px] uppercase tracking-widest text-red-600 dark:text-red-400 mb-1">Sera supprimé</div>
              <ul className="text-xs text-slate-700 dark:text-slate-300 space-y-0.5">
                <li>• Dossiers, réclamations, appels</li>
                <li>• Clients / assurés</li>
                <li>• Tickets, messages, discussions</li>
                <li>• Documents (GED)</li>
                <li>• Notifications, historique</li>
                <li>• Prospects (demandes d&apos;inscription)</li>
                <li>• Base de connaissances</li>
              </ul>
            </div>
            <div className="rounded-lg border border-emerald-200 dark:border-emerald-900 bg-white dark:bg-slate-900 p-3">
              <div className="text-[10px] uppercase tracking-widest text-emerald-700 dark:text-emerald-400 mb-1">Sera préservé</div>
              <ul className="text-xs text-slate-700 dark:text-slate-300 space-y-0.5">
                <li>• Utilisateurs & codes d&apos;accès</li>
                <li>• Paramètres entreprise</li>
                <li>• Logo & couleurs</li>
                <li>• Messages borne/écran</li>
                <li>• Services de la borne</li>
                <li>• Sessions actives</li>
              </ul>
            </div>
          </div>
          <Button
            variant="destructive"
            onClick={() => { setConfirmText(""); setResult(null); setOpen(true); }}
            className="bg-red-600 hover:bg-red-700 text-white"
            data-testid="danger-open-btn"
          >
            <Trash2 className="w-4 h-4 mr-2" /> Remettre à zéro les données
          </Button>

          <AlertDialog open={open} onOpenChange={setOpen}>
            <AlertDialogContent data-testid="danger-confirm-dialog">
              {result ? (
                <>
                  <AlertDialogHeader>
                    <AlertDialogTitle className="flex items-center gap-2 text-emerald-700">
                      <ShieldAlert className="w-5 h-5" /> Remise à zéro terminée
                    </AlertDialogTitle>
                    <AlertDialogDescription>
                      <b>{totalDeleted}</b> document(s) supprimé(s) au total. Vos utilisateurs, paramètres et branding sont intacts.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <div className="max-h-64 overflow-y-auto text-xs font-mono bg-slate-50 dark:bg-slate-900 rounded p-3 border border-slate-200 dark:border-slate-800">
                    {Object.entries(result.deleted || {}).map(([k, v]) => (
                      <div key={k} className="flex justify-between">
                        <span className="text-slate-500">{k}</span>
                        <span className={typeof v === "number" && v > 0 ? "text-red-600" : "text-slate-400"}>{String(v)}</span>
                      </div>
                    ))}
                  </div>
                  <AlertDialogFooter>
                    <AlertDialogAction
                      onClick={() => { setOpen(false); window.location.reload(); }}
                      className="bg-blue-900 hover:bg-blue-800"
                      data-testid="danger-close-reload"
                    >
                      Recharger l&apos;application
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </>
              ) : (
                <>
                  <AlertDialogHeader>
                    <AlertDialogTitle className="flex items-center gap-2 text-red-700">
                      <AlertTriangle className="w-5 h-5" /> Action irréversible
                    </AlertDialogTitle>
                    <AlertDialogDescription className="space-y-3">
                      <span className="block">
                        Vous êtes sur le point de supprimer <b>toutes les données métier</b>. Cette action est <b>irréversible</b> et ne peut pas être annulée.
                      </span>
                      <span className="block">
                        Pour confirmer, saisissez exactement <code className="px-1.5 py-0.5 bg-red-100 dark:bg-red-950/40 text-red-800 dark:text-red-300 rounded font-mono text-xs">{CONFIRM_STRING}</code> ci-dessous :
                      </span>
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <Input
                    autoFocus
                    value={confirmText}
                    onChange={(e) => setConfirmText(e.target.value)}
                    placeholder={CONFIRM_STRING}
                    className="font-mono"
                    data-testid="danger-confirm-input"
                  />
                  <AlertDialogFooter>
                    <AlertDialogCancel disabled={running} data-testid="danger-cancel">Annuler</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={runReset}
                      disabled={running || confirmText !== CONFIRM_STRING}
                      className="bg-red-600 hover:bg-red-700 text-white disabled:opacity-40"
                      data-testid="danger-confirm-btn"
                    >
                      {running ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Suppression…</>
                      ) : (
                        <><Trash2 className="w-4 h-4 mr-2" /> Supprimer maintenant</>
                      )}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </>
              )}
            </AlertDialogContent>
          </AlertDialog>
        </CardContent>
      </Card>
    </>
  );
}
